import {Controller} from "stimulus";

declare const ace;

export default class CodeEditorController extends Controller<HTMLTextAreaElement> {
  declare controlField?: HTMLElement;
  declare editor?: any;

  connect() {
    this.injectAceScript();

    // Wait for ace to be loaded after script being injected
    const interval = setInterval(() => {
      if (ace) {
        this.handleInitAce();
        this.element.addEventListener("turbo:morph-element", this.reInit);
        clearInterval(interval);
      }
    }, 100);

    setTimeout(() => {
      clearInterval(interval);
    }, 5000);
  }

  disconnect() {
    this.editor?.destroy();
    this.controlField?.remove();
    this.element.removeEventListener("turbo:morph-element", this.reInit);
  }

  handleInitAce = () => {
    this.controlField = document.createElement("div");
    const rect = this.element.getBoundingClientRect();

    this.controlField.classList.add(...this.element.classList);

    this.controlField.style.setProperty("width", rect.width + "px");
    this.controlField.style.setProperty("height", rect.height + "px");
    this.element.after(this.controlField);
    this.element.classList.add("visibility", "hidden");

    this.editor = ace.edit(this.controlField, {
      showPrintMargin: false,
      tabSize: 2,
    });
    this.editor.getSession().setMode("ace/mode/json");

    this.editor.session.on("change", () => {
      this.element.value = this.editor.getSession().getValue();
    });

    this.editor.session.on("changeAnnotation", () => {
      const elementId = this.element.dataset.disableableElementDataIdentifier;
      const element = document.querySelector(`[data-code-editor-diseableable-element-id="${elementId}"]`) as HTMLButtonElement | null;

      if (element === null) { return; }

      const annotations = this.editor.getSession().getAnnotations();
      if (annotations.length > 0) {
        element.disabled = true;
        element.textContent = element.dataset.disabledText ?? null;
      } else {
        element.disabled = false;
        element.textContent = element.dataset.enabledText ?? null;
      }
    });

    this.editor.getSession().setValue(this.safeStringifyJSON(this.element.value));
  };

  reInit = (e: Event) => {
    if (e.target === this.element) {
      setTimeout(() => {
        this.editor?.destroy();
        this.controlField?.remove();
        this.handleInitAce();
      });
    }
  };

  safeStringifyJSON(input: any) {
    if (typeof input === "string") {
      try {
        const a = JSON.parse(input);
        return JSON.stringify(a, null, "\t");
      } catch {
        return input;
      }
    } else {
      return JSON.stringify(input, null, "\t");
    }
  }

  injectAceScript() {
    // We tried using the bundled version of ace instead of this
    // but it broke the built in linter.
    const script = document.createElement("script");
    script.type = "application/javascript";
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/ace/1.42.0/ace.min.js";
    script.integrity = "sha512-qg6UMWpaeWQGeFaZPyuDXKnureIwMkVqhCCF7GdiWv7bsEDQHVQq3U08ABGNNJFLNM1IZNBJfXGNbpJNl2dkNg==";
    script.crossOrigin = "anonymous";
    script.referrerPolicy = "no-referrer";
    document.head.appendChild(script);
  }
}
